/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo } from 'react'

import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { App, Modal } from 'antd'

import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'

import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import ShareButtons from './ShareButtons'

export type ShareEntityType =
  | 'game'
  | 'team'
  | 'schedule'
  | 'organization'
  | 'player'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: ShareEntityType
  entityName: string
  shareUrl: string
  shareDescription?: string
}

const ENTITY_TYPE_LABELS: Record<ShareEntityType, string> = {
  game: 'Game',
  team: 'Team',
  schedule: 'Schedule',
  organization: 'Organization',
  player: 'Player',
}

const modalStyles = {
  header: {
    backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
    paddingLeft: MODAL_TOKENS.PADDING_HEADER,
    paddingRight: MODAL_TOKENS.PADDING_HEADER,
  },
  body: {
    borderTop: `1px solid ${colors.SECONDARY_100}`,
    backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
  },
  content: {
    backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: MODAL_TOKENS.Z_INDEX_WRAPPER,
  },
  mask: {
    backdropFilter: `blur(${MODAL_TOKENS.MASK_BLUR})`,
    backgroundColor: MODAL_TOKENS.MASK_COLOR,
    zIndex: MODAL_TOKENS.Z_INDEX_MASK,
  },
  footer: {
    display: 'none' as const,
  },
}

/**
 * Modal for sharing an entity (game, team, schedule, etc.) via social
 * platforms or a copy-to-clipboard link. Handles UTM tracking and
 * mobile-responsive layout.
 *
 * @param isOpen - Controls modal visibility
 * @param onClose - Callback when the modal is dismissed
 * @param entityType - The type of entity being shared
 * @param entityName - Display name used in the share message
 * @param shareUrl - Base URL to share
 * @param shareDescription - Optional custom description for platforms that support it
 */
function ShareModal({
  isOpen,
  onClose,
  entityType,
  entityName,
  shareUrl,
  shareDescription,
}: ShareModalProps) {
  const { width } = useWindowSize()
  const isMobile = useMemo(
    () => width <= MODAL_TOKENS.MOBILE_BREAKPOINT,
    [width]
  )
  const { openModal, closeModal } = useModalScroll()
  const { message } = App.useApp()

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  const entityLabel = ENTITY_TYPE_LABELS[entityType]

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Share {entityLabel}
      </Text>
    )
  }, [entityLabel])

  const shareTitle = useMemo(() => {
    return `Check out ${entityName} on ${
      isCanlan ? 'Canlan Stats' : 'SportNinja'
    }!`
  }, [entityName])

  const copyUrl = useMemo(() => {
    const separator = shareUrl.includes('?') ? '&' : '?'
    return `${shareUrl}${separator}utm_source=copy_link&utm_medium=social&utm_campaign=share&utm_content=${entityType}`
  }, [shareUrl, entityType])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(copyUrl)
      message.success('Link copied to clipboard!')
    } catch (error) {
      console.error('🚨 Failed to copy link:', error)
      message.error('Failed to copy link')
    }
  }, [copyUrl, message])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100%' : 'auto'} !important;
        margin: ${isMobile ? '0' : `${MODAL_TOKENS.MARGIN_DESKTOP} auto`};
        padding: 0;

        .ant-modal-content {
          height: ${isMobile ? '100%' : 'auto'} !important;
          border-radius: ${isMobile
            ? MODAL_TOKENS.BORDER_RADIUS_MOBILE
            : MODAL_TOKENS.BORDER_RADIUS}px;
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
      `}
      width={isMobile ? '100%' : 620}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={null}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      styles={modalStyles}
      maskClosable={true}
      destroyOnClose
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
        `}
      >
        {/* Share to section */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <Text variant='paragraphMedium' weight='medium' color={colors.WHITE}>
            Share to
          </Text>
          <ShareButtons
            shareUrl={shareUrl}
            shareTitle={shareTitle}
            shareDescription={shareDescription}
            entityType={entityType}
          />
        </div>

        {/* Divider */}
        <div
          css={css`
            height: 1px;
            background-color: ${colors.SECONDARY_100};
            width: 100%;
          `}
        />

        {/* Copy link section */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <Text variant='paragraphMedium' weight='medium' color={colors.WHITE}>
            Copy link
          </Text>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              background-color: ${colors.SECONDARY_400};
              border: 1px solid ${colors.SECONDARY_200};
              border-radius: 8px;
              padding: 8px 8px 8px 16px;
            `}
          >
            <span
              css={css`
                flex: 1;
                font-size: 14px;
                font-weight: 400;
                color: ${colors.WHITE};
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                min-width: 0;
                line-height: 20px;
              `}
            >
              {shareUrl}
            </span>
            <button
              onClick={handleCopy}
              css={css`
                flex-shrink: 0;
                background-color: ${colors.PRIMARY};
                color: ${colors.SECONDARY_300};
                border: none;
                border-radius: 24px;
                padding: 4px 16px;
                font-size: 14px;
                font-weight: 500;
                line-height: 20px;
                cursor: pointer;
                transition: background-color 0.15s ease;

                &:hover {
                  background-color: ${colors.PRIMARY_200};
                }

                &:active {
                  background-color: ${colors.PRIMARY_400};
                }
              `}
            >
              Copy
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ShareModal
